// Strava OAuth 2.0 認証フローを管理するメインクラス

import axios from 'axios';
import type { AuthToken, StravaAthlete, OAuthConfig, TokenResponse } from './types';
import { saveToken, getToken, removeToken, isTokenValid } from './tokenStorage';

export class AuthManager {
  private config: OAuthConfig;
  private static instance: AuthManager | null = null;

  constructor(config?: Partial<OAuthConfig>) {
    this.config = {
      clientId: config?.clientId || import.meta.env.VITE_STRAVA_CLIENT_ID || '',
      redirectUri: config?.redirectUri || `${window.location.origin}/auth/callback`,
      scope: config?.scope || ['activity:read_all', 'profile:read_all']
    };

    if (!this.config.clientId) {
      throw new Error('Strava Client ID が設定されていません。環境変数 VITE_STRAVA_CLIENT_ID を確認してください。');
    }
  }

  /**
   * シングルトンインスタンスを取得
   */
  static getInstance(config?: Partial<OAuthConfig>): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager(config);
    }
    return AuthManager.instance;
  }

  /**
   * OAuth認証URLを生成
   */
  generateAuthUrl(): string {
    const state = this.generateRandomState();
    sessionStorage.setItem('oauth_state', state);

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      response_type: 'code',
      redirect_uri: this.config.redirectUri,
      approval_prompt: 'force',
      scope: this.config.scope.join(','),
      state: state
    });

    return `https://www.strava.com/oauth/authorize?${params.toString()}`;
  }

  /**
   * 認証コードをアクセストークンに交換
   */
  async exchangeCodeForToken(code: string, state: string): Promise<AuthToken> {
    // CSRF攻撃防止のためのstate検証
    const savedState = sessionStorage.getItem('oauth_state');
    if (state !== savedState) {
      throw new Error('Invalid state parameter. 認証プロセスが改ざんされている可能性があります。');
    }

    try {
      // 開発環境では直接Strava APIを使用（本番ではPages Function経由）
      const isDevelopment = import.meta.env.DEV;
      
      const response = isDevelopment 
        ? await axios.post<TokenResponse>('https://www.strava.com/oauth/token', {
            client_id: this.config.clientId,
            client_secret: import.meta.env.VITE_STRAVA_CLIENT_SECRET || import.meta.env.STRAVA_CLIENT_SECRET,
            code: code,
            grant_type: 'authorization_code'
          })
        : await axios.post<TokenResponse>('/auth/token', {
            code: code,
            state: state
          }, {
            headers: {
              'X-Client-ID': this.config.clientId
            }
          });

      const tokenData = response.data;
      const authToken: AuthToken = {
        access_token: tokenData.access_token,
        refresh_token: tokenData.refresh_token,
        expires_at: tokenData.expires_at,
        scope: this.config.scope,
        token_type: tokenData.token_type
      };

      // トークンを安全に保存
      saveToken(authToken);
      
      // state をクリーンアップ
      sessionStorage.removeItem('oauth_state');

      return authToken;
    } catch (error) {
      console.error('Token exchange failed:', error);
      throw new Error('認証トークンの取得に失敗しました。再度認証を行ってください。');
    }
  }

  /**
   * 現在の認証状態を取得
   */
  getCurrentToken(): AuthToken | null {
    return getToken();
  }

  /**
   * ユーザーがログイン済みかチェック
   */
  isAuthenticated(): boolean {
    const token = getToken();
    return isTokenValid(token);
  }

  /**
   * トークンのリフレッシュ
   */
  async refreshToken(refreshToken: string): Promise<AuthToken> {
    try {
      // Cloudflare Pages Functionを経由してトークンリフレッシュ（セキュア）
      const response = await axios.post<TokenResponse>('/auth/refresh', {
        refresh_token: refreshToken
      }, {
        headers: {
          'X-Client-ID': this.config.clientId
        }
      });

      const tokenData = response.data;
      const authToken: AuthToken = {
        access_token: tokenData.access_token,
        refresh_token: tokenData.refresh_token,
        expires_at: tokenData.expires_at,
        scope: this.config.scope,
        token_type: tokenData.token_type
      };

      saveToken(authToken);
      return authToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.logout(); // リフレッシュ失敗時はログアウト
      throw new Error('認証の更新に失敗しました。再度ログインしてください。');
    }
  }

  /**
   * ログアウト処理
   */
  logout(): void {
    removeToken();
    sessionStorage.removeItem('oauth_state');
  }

  /**
   * 認証済みのアスリート情報を取得
   */
  async getAuthenticatedAthlete(): Promise<StravaAthlete> {
    const token = this.getCurrentToken();
    if (!token) {
      throw new Error('認証が必要です。ログインしてください。');
    }

    try {
      const response = await axios.get<StravaAthlete>('https://www.strava.com/api/v3/athlete', {
        headers: {
          'Authorization': `Bearer ${token.access_token}`
        }
      });

      return response.data;
    } catch (error) {
      console.error('Failed to get athlete info:', error);
      
      // トークンが無効な場合、リフレッシュを試行
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        try {
          const newToken = await this.refreshToken(token.refresh_token);
          const response = await axios.get<StravaAthlete>('https://www.strava.com/api/v3/athlete', {
            headers: {
              'Authorization': `Bearer ${newToken.access_token}`
            }
          });
          return response.data;
        } catch (refreshError) {
          throw new Error('認証の更新に失敗しました。再度ログインしてください。');
        }
      }

      throw new Error('アスリート情報の取得に失敗しました。');
    }
  }

  /**
   * ランダムなstate文字列を生成（CSRF攻撃防止）
   */
  private generateRandomState(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
}